import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CloseOutlined } from '@ant-design/icons'

/**
 * Bottom sheet: the phone-sized equivalent of a side panel, and the pattern
 * trading apps use for order entry because it keeps the thumb near the action.
 * Escape and the backdrop close it, focus moves inside on open and returns
 * where it came from on close, and the page behind does not scroll.
 *
 * Rendered through a portal on purpose. `.view` carries an entrance animation,
 * and an animated element forms a stacking context, so a sheet rendered inside
 * the page has its z-index resolved *within* that context — the fixed tab bar,
 * a sibling of the view, then paints over the sheet and swallows the submit
 * button. Escaping to <body> puts the sheet in the same stacking context as the
 * tab bar, where its higher z-index actually wins.
 */
export default function Sheet({
  open, title, onClose, children,
}: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)

  // Callers pass an inline arrow, so `onClose` is a new function on every
  // render of the page — and the page re-renders every few seconds as the book
  // and tape poll. Keeping it in a ref lets the effect below depend on `open`
  // alone; with `onClose` in the dependency list it tore down and re-ran on
  // every poll, moving focus out of whatever field was being typed into and
  // dismissing the keyboard on iOS.
  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })

  useEffect(() => {
    if (!open) return
    restoreTo.current = document.activeElement as HTMLElement
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    document.addEventListener('keydown', onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // move focus to the first control in the sheet
    panel.current?.querySelector<HTMLElement>('button, input, [tabindex]:not([tabindex="-1"])')?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      restoreTo.current?.focus?.()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        ref={panel}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={e => e.stopPropagation()}
      >
        <div className="sheet-head">
          <span className="sheet-grip" aria-hidden />
          <strong className="grow">{title}</strong>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><CloseOutlined /></button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
